import React, { useState, useMemo } from 'react';
import { Calculator, Landmark, ShieldCheck, PiggyBank } from 'lucide-react';

const inr = (n) => '₹' + Math.round(Number(n) || 0).toLocaleString('en-IN');

const Field = ({ label, value, onChange, suffix, testid }) => (
  <label className="block">
    <span className="text-xs font-medium text-slate-600">{label}</span>
    <div className="mt-1 flex items-center rounded-lg border border-slate-300 focus-within:border-emerald-500 focus-within:ring-1 focus-within:ring-emerald-500 bg-white">
      <input
        type="number"
        inputMode="numeric"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2.5 text-sm bg-transparent outline-none rounded-lg"
        data-testid={testid}
      />
      {suffix && <span className="px-3 text-xs text-slate-400 whitespace-nowrap">{suffix}</span>}
    </div>
  </label>
);

const Result = ({ label, value, accent = 'text-emerald-700', testid }) => (
  <div className="rounded-lg bg-slate-50 p-3 text-center">
    <p className={`text-lg font-black ${accent}`} data-testid={testid}>{value}</p>
    <p className="text-[11px] text-slate-500 mt-0.5">{label}</p>
  </div>
);

const EmiCalculator = () => {
  const [amount, setAmount] = useState('500000');
  const [rate, setRate] = useState('11');
  const [years, setYears] = useState('5');
  const { emi, totalInterest, total } = useMemo(() => {
    const P = parseFloat(amount) || 0;
    const r = (parseFloat(rate) || 0) / 12 / 100;
    const n = (parseFloat(years) || 0) * 12;
    if (!P || !n) return { emi: 0, totalInterest: 0, total: 0 };
    const emi = r === 0 ? P / n : (P * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
    const total = emi * n;
    return { emi, totalInterest: total - P, total };
  }, [amount, rate, years]);

  return (
    <div className="space-y-4" data-testid="calc-emi">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Field label="Loan amount" value={amount} onChange={setAmount} suffix="₹" testid="emi-amount" />
        <Field label="Interest rate" value={rate} onChange={setRate} suffix="% p.a." testid="emi-rate" />
        <Field label="Tenure" value={years} onChange={setYears} suffix="years" testid="emi-years" />
      </div>
      <div className="grid grid-cols-3 gap-3">
        <Result label="Monthly EMI" value={inr(emi)} testid="emi-result" />
        <Result label="Total interest" value={inr(totalInterest)} accent="text-amber-700" testid="emi-interest" />
        <Result label="Total payable" value={inr(total)} accent="text-slate-800" testid="emi-total" />
      </div>
    </div>
  );
};

const CoverCalculator = () => {
  const [income, setIncome] = useState('600000');
  const [ageYears, setAgeYears] = useState('30');
  const [loans, setLoans] = useState('1000000');
  const [existing, setExisting] = useState('0');
  const { cover } = useMemo(() => {
    const inc = parseFloat(income) || 0;
    const age = parseFloat(ageYears) || 30;
    const supportYears = Math.max(60 - age, 5);           // years income must be replaced (until 60)
    const need = inc * supportYears + (parseFloat(loans) || 0) - (parseFloat(existing) || 0);
    return { cover: Math.max(need, 0) };
  }, [income, ageYears, loans, existing]);

  return (
    <div className="space-y-4" data-testid="calc-cover">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Annual income" value={income} onChange={setIncome} suffix="₹" testid="cover-income" />
        <Field label="Your age" value={ageYears} onChange={setAgeYears} suffix="years" testid="cover-age" />
        <Field label="Outstanding loans" value={loans} onChange={setLoans} suffix="₹" testid="cover-loans" />
        <Field label="Existing cover" value={existing} onChange={setExisting} suffix="₹" testid="cover-existing" />
      </div>
      <div className="grid grid-cols-1 gap-3">
        <Result label="Recommended life cover (income till age 60 + loans − existing)" value={inr(cover)} testid="cover-result" />
      </div>
    </div>
  );
};

const FdCalculator = () => {
  const [principal, setPrincipal] = useState('100000');
  const [rate, setRate] = useState('7.5');
  const [years, setYears] = useState('5');
  const { maturity, interest } = useMemo(() => {
    const P = parseFloat(principal) || 0;
    const r = (parseFloat(rate) || 0) / 100;
    const t = parseFloat(years) || 0;
    const maturity = P * Math.pow(1 + r / 4, 4 * t);      // quarterly compounding
    return { maturity, interest: maturity - P };
  }, [principal, rate, years]);

  return (
    <div className="space-y-4" data-testid="calc-fd">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Field label="Deposit amount" value={principal} onChange={setPrincipal} suffix="₹" testid="fd-principal" />
        <Field label="Interest rate" value={rate} onChange={setRate} suffix="% p.a." testid="fd-rate" />
        <Field label="Tenure" value={years} onChange={setYears} suffix="years" testid="fd-years" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Result label="Interest earned" value={inr(interest)} accent="text-amber-700" testid="fd-interest" />
        <Result label="Maturity value" value={inr(maturity)} testid="fd-result" />
      </div>
    </div>
  );
};

const TABS = [
  { key: 'emi', label: 'Loan EMI', icon: Landmark, node: <EmiCalculator /> },
  { key: 'cover', label: 'Insurance cover', icon: ShieldCheck, node: <CoverCalculator /> },
  { key: 'fd', label: 'FD returns', icon: PiggyBank, node: <FdCalculator /> },
];

const BenefitCalculators = () => {
  const [tab, setTab] = useState('emi');
  const active = TABS.find((t) => t.key === tab) || TABS[0];

  return (
    <section className="mb-10" data-testid="foryou-calculators">
      <div className="flex items-center gap-2 mb-3">
        <Calculator className="h-4 w-4 text-emerald-600" />
        <h2 className="text-lg sm:text-xl font-bold text-gray-900">Quick calculators</h2>
      </div>
      <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
        <div className="flex flex-wrap gap-2 mb-5">
          {TABS.map((t) => {
            const Icon = t.icon;
            const on = t.key === tab;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                data-testid={`calc-tab-${t.key}`}
                className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium transition ${
                  on ? 'bg-emerald-700 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                <Icon className="h-4 w-4" />
                {t.label}
              </button>
            );
          })}
        </div>
        {active.node}
        <p className="mt-4 text-[11px] text-slate-400">
          Estimates only, for planning. Actual EMI, premiums and returns depend on the provider's terms.
        </p>
      </div>
    </section>
  );
};

export default BenefitCalculators;
